import { useState } from 'react';
import { Building2, RefreshCw, Trash2, Wallet, CheckCircle, AlertCircle, Clock, Users, Pencil, Check, X } from 'lucide-react';
import { colors } from '../../styles/colors';

/**
 * ConnectionCard - Carte de connexion bancaire
 *
 * Refactorisé avec:
 * - Sous-composants organisés
 * - Helpers extraits
 * - Styles centralisés
 */
function ConnectionCard({
  connection,
  accounts,
  onSync,
  onDelete,
  onManageUsers,
  onRename,
  canEdit,
  isLoading,
  onAccountSelect,
  selectedAccountId,
  authorizedUsers = []
}) {
  const connectionType = connection.connection_type;
  const bankName = connection.bank_name || 'Banque';
  const connectionName = connection.connection_name;
  const lastSync = connection.last_sync;
  const status = connection.status || 'active';
  const accountsCount = connection.accounts_count || 0;
  const connectionAccounts = connection.accounts || [];

  return (
    <div
      style={cardStyle}
      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)'}
      onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'}
    >
      {/* Header */}
      <CardHeader
        bankName={bankName}
        connectionName={connectionName}
        status={status}
        canEdit={canEdit}
        isLoading={isLoading}
        onManageUsers={() => onManageUsers && onManageUsers(connection)}
        onDelete={() => onDelete(connection)}
        onRename={onRename ? (name) => onRename(connection.id, name) : null}
      />

      {/* Informations */}
      <ConnectionInfo
        connectionType={connectionType}
        lastSync={lastSync}
        accountsCount={connectionAccounts.length || accountsCount}
      />

      {/* Utilisateurs autorisés */}
      {authorizedUsers && authorizedUsers.length > 0 && (
        <AuthorizedUsersSection users={authorizedUsers} />
      )}

      {/* Comptes bancaires */}
      {connectionAccounts.length > 0 && (
        <AccountsList
          accounts={connectionAccounts}
          selectedAccountId={selectedAccountId}
          onAccountSelect={onAccountSelect}
        />
      )}

      {/* Boutons d'action */}
      {onSync && (
        <SyncButton
          isLoading={isLoading}
          onClick={() => onSync(connection.id)}
          label={isLoading ? 'Synchronisation...' : 'Synchroniser'}
          color={colors.primary.teal}
          hoverColor={colors.primary.tealDark}
        />
      )}
    </div>
  );
}

// === HELPERS ===

const formatBalance = (balance, currency = 'EUR') => {
  if (balance === null || balance === undefined || isNaN(balance)) return '0,00 €';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(balance);
};

const formatDate = (dateString) => {
  if (!dateString) return 'Jamais';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const STATUS_CONFIG = {
  active: { color: colors.status.success, icon: CheckCircle, label: 'Active' },
  error: { color: colors.status.error, icon: AlertCircle, label: 'Erreur' },
  inactive: { color: colors.text.secondary, icon: Clock, label: 'Inactive' }
};

const getStatusConfig = (status) => STATUS_CONFIG[status] || { color: colors.text.secondary, icon: null, label: status };

// === SOUS-COMPOSANTS ===

function CardHeader({ bankName, connectionName, status, canEdit, isLoading, onManageUsers, onDelete, onRename }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const statusConfig = getStatusConfig(status);
  const StatusIcon = statusConfig.icon;

  const startEdit = () => {
    setEditValue(connectionName || '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditValue('');
  };

  const saveEdit = async () => {
    if (!onRename) return;
    setSaving(true);
    try {
      await onRename(editValue.trim());
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  return (
    <div style={headerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={iconContainerStyle}>
          <Building2 size={24} color={colors.primary.teal} />
        </div>
        <div>
          {editing ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                autoFocus
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={100}
                disabled={saving}
                style={{
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: colors.text.primary,
                  border: `1px solid ${colors.primary.teal}`,
                  borderRadius: '0.25rem',
                  padding: '0.125rem 0.375rem',
                  outline: 'none',
                  width: '160px'
                }}
              />
              <ActionButton onClick={saveEdit} disabled={saving} icon={<Check size={16} />} title="Enregistrer" hoverBg={colors.primary.teal + '15'} hoverColor={colors.primary.teal} />
              <ActionButton onClick={cancelEdit} disabled={saving} icon={<X size={16} />} title="Annuler" hoverBg="#FEE2E2" hoverColor="#DC2626" />
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <h3 style={bankNameStyle}>{connectionName || bankName}</h3>
              {canEdit && onRename && (
                <button onClick={startEdit} style={{ ...actionButtonStyle, padding: '0.125rem' }} title="Renommer" onMouseEnter={(e) => { e.currentTarget.style.color = colors.primary.teal; }} onMouseLeave={(e) => { e.currentTarget.style.color = colors.text.secondary; }}>
                  <Pencil size={14} />
                </button>
              )}
            </div>
          )}
          {!editing && connectionName && (
            <p style={{ fontSize: '0.75rem', color: colors.text.secondary, margin: '0 0 0.125rem 0' }}>{bankName}</p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem', color: statusConfig.color }}>
            {StatusIcon && <StatusIcon size={16} />}
            <span style={{ fontSize: '0.75rem', fontWeight: '500' }}>{statusConfig.label}</span>
          </div>
        </div>
      </div>

      {canEdit && (
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <ActionButton
            onClick={onManageUsers}
            disabled={isLoading}
            icon={<Users size={18} />}
            title="Gérer les utilisateurs autorisés"
            hoverBg={colors.primary.teal + '15'}
            hoverColor={colors.primary.teal}
          />
          <ActionButton
            onClick={onDelete}
            disabled={isLoading}
            icon={<Trash2 size={18} />}
            title="Supprimer la connexion"
            hoverBg="#FEE2E2"
            hoverColor="#DC2626"
          />
        </div>
      )}
    </div>
  );
}

function ActionButton({ onClick, disabled, icon, title, hoverBg, hoverColor }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ ...actionButtonStyle, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = hoverBg;
          e.currentTarget.style.color = hoverColor;
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = colors.text.secondary;
        }
      }}
      title={title}
    >
      {icon}
    </button>
  );
}

function ConnectionInfo({ connectionType, lastSync, accountsCount }) {
  return (
    <div style={infoContainerStyle}>
      <InfoRow label="Type de connexion" value={connectionType === 'api' ? 'API' : 'Import fichier'} />
      <InfoRow label="Dernière synchronisation" value={formatDate(lastSync)} />
      <InfoRow label="Nombre de comptes" value={accountsCount} isLast />
    </div>
  );
}

function InfoRow({ label, value, isLast = false }) {
  return (
    <div style={{ ...infoRowStyle, marginBottom: isLast ? 0 : '0.75rem' }}>
      <span style={infoLabelStyle}>{label}</span>
      <span style={infoValueStyle}>{value}</span>
    </div>
  );
}

function AuthorizedUsersSection({ users }) {
  return (
    <div style={usersContainerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <Users size={14} color={colors.primary.teal} />
        <span style={usersTitleStyle}>Utilisateurs autorisés</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
        {users.map(user => (
          <span key={user.id} style={userBadgeStyle}>
            {user.prenom} {user.nom}
          </span>
        ))}
      </div>
    </div>
  );
}

function AccountsList({ accounts, selectedAccountId, onAccountSelect }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <h4 style={accountsTitleStyle}>Comptes</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {accounts.map(account => (
          <AccountItem
            key={account.id}
            account={account}
            isSelected={selectedAccountId === account.id}
            onSelect={() => onAccountSelect(account.id)}
          />
        ))}
      </div>
    </div>
  );
}

function AccountItem({ account, isSelected, onSelect }) {
  const accountName = account.account_name || account.name || 'Compte sans nom';
  const balance = account.balance;
  const currency = account.currency || 'EUR';
  const iban = account.iban;

  return (
    <div
      onClick={onSelect}
      style={{
        ...accountItemStyle,
        backgroundColor: isSelected ? colors.primary.teal + '10' : colors.background.light,
        borderColor: isSelected ? colors.primary.teal : colors.border.light
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = colors.primary.teal;
          e.currentTarget.style.backgroundColor = colors.background.white;
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = colors.border.light;
          e.currentTarget.style.backgroundColor = colors.background.light;
        }
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Wallet size={16} color={colors.text.secondary} />
        <div>
          <div style={accountNameStyle}>{accountName}</div>
          {iban && <div style={ibanStyle}>{iban}</div>}
        </div>
      </div>
      <div style={{ ...balanceStyle, color: balance >= 0 ? colors.status.success : colors.status.error }}>
        {formatBalance(balance, currency)}
      </div>
    </div>
  );
}

function SyncButton({ isLoading, onClick, label, color, hoverColor }) {
  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      style={{
        ...syncButtonStyle,
        backgroundColor: isLoading ? colors.border.medium : color,
        cursor: isLoading ? 'not-allowed' : 'pointer'
      }}
      onMouseEnter={(e) => {
        if (!isLoading && hoverColor) {
          e.target.style.backgroundColor = hoverColor;
        } else if (!isLoading) {
          e.target.style.opacity = '0.8';
        }
      }}
      onMouseLeave={(e) => {
        if (!isLoading) {
          e.target.style.backgroundColor = color;
          e.target.style.opacity = '1';
        }
      }}
    >
      <RefreshCw size={16} style={{ animation: isLoading ? 'spin 1s linear infinite' : 'none' }} />
      {label}
    </button>
  );
}

// === STYLES ===

const cardStyle = {
  backgroundColor: colors.background.white,
  borderRadius: '0.75rem',
  padding: '1.5rem',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  border: `1px solid ${colors.border.light}`,
  transition: 'all 0.2s',
  fontFamily: '"Noto Sans", sans-serif'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '1rem'
};

const iconContainerStyle = {
  width: '48px',
  height: '48px',
  borderRadius: '0.5rem',
  backgroundColor: colors.background.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const bankNameStyle = {
  fontSize: '1.125rem',
  fontWeight: '600',
  color: colors.text.primary,
  margin: 0
};

const actionButtonStyle = {
  padding: '0.5rem',
  border: 'none',
  backgroundColor: 'transparent',
  color: colors.text.secondary,
  borderRadius: '0.25rem',
  transition: 'all 0.2s'
};

const infoContainerStyle = {
  padding: '1rem',
  backgroundColor: colors.background.light,
  borderRadius: '0.5rem',
  marginBottom: '1rem'
};

const infoRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const infoLabelStyle = {
  fontSize: '0.875rem',
  color: colors.text.secondary
};

const infoValueStyle = {
  fontSize: '0.875rem',
  fontWeight: '500',
  color: colors.text.primary
};

const usersContainerStyle = {
  padding: '0.75rem 1rem',
  backgroundColor: colors.primary.teal + '08',
  borderRadius: '0.5rem',
  marginBottom: '1rem',
  border: `1px solid ${colors.primary.teal}20`
};

const usersTitleStyle = {
  fontSize: '0.75rem',
  color: colors.primary.teal,
  fontWeight: '500'
};

const userBadgeStyle = {
  padding: '0.25rem 0.5rem',
  backgroundColor: colors.text.white,
  borderRadius: '0.25rem',
  fontSize: '0.75rem',
  color: colors.text.primary,
  border: `1px solid ${colors.border.light}`
};

const accountsTitleStyle = {
  fontSize: '0.875rem',
  fontWeight: '600',
  color: colors.text.primary,
  marginBottom: '0.75rem'
};

const accountItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.75rem',
  borderRadius: '0.375rem',
  border: '2px solid',
  cursor: 'pointer',
  transition: 'all 0.2s'
};

const accountNameStyle = {
  fontSize: '0.875rem',
  fontWeight: '500',
  color: colors.text.primary
};

const ibanStyle = {
  fontSize: '0.75rem',
  color: colors.text.secondary,
  fontFamily: 'monospace'
};

const balanceStyle = {
  fontSize: '0.875rem',
  fontWeight: '600'
};

const syncButtonStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  padding: '0.75rem',
  color: colors.background.white,
  border: 'none',
  borderRadius: '0.5rem',
  fontSize: '0.875rem',
  fontWeight: '500',
  transition: 'all 0.2s',
  fontFamily: '"Noto Sans", sans-serif'
};

export default ConnectionCard;
